import React, { useContext, useEffect, useReducer } from 'react';
import { changeCategoryPositionReducer } from './changeCategoryPositionReducer';
import { ChangeCategoryPositionActionTypes } from './changeCategoryPositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { ICategoryPositionItemV2, IGetCategoryPositionListV2 } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import {
	ChangeCategoryPositionContext,
	changeCategoryPositionInitialState,
	TChangeCategoryPositionSelectedRestaurant,
	TTypeOfChangeCategoryPositionModalWindow,
} from './changeCategoryPositionContext';
import { getCategoryPositionListV2, getCategoryPhotos } from 'backend/categories';
import { createAppError } from 'utils/helpers';

export const ChangeCategoryPositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changeCategoryPositionReducer, changeCategoryPositionInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<IGetCategoryPositionListV2, string>(
		state.selectedRestaurant ? getCategoryPositionListV2 : null,
		state.selectedRestaurant?.id || '',
	);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ChangeCategoryPositionActionTypes.SET_POSITIONS_DATA,
				payload: {
					positionsData: response.data.categories,
				},
			});

			const getCategoriesPreSignedUrl = async (categoryIds: string[]) => {
				try {
					const categoryPhotosResponse = await getCategoryPhotos({ categoryIds, size: 1 });
					const updatedCategories = response.data.categories.reduce(
						(acc: ICategoryPositionItemV2[], item) => {
							const categoryPhotoFile = categoryPhotosResponse.data.files.find(
								(photoFile) => photoFile.id === item.id,
							);
							acc.push({
								...item,
								preSignedUrl: categoryPhotoFile ? categoryPhotoFile.preSignedUrl : null,
							});
							return acc;
						},
						[],
					);

					dispatch({
						type: ChangeCategoryPositionActionTypes.SET_POSITIONS_DATA,
						payload: {
							positionsData: updatedCategories,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const categoryIds = response.data.categories.map((category) => category.id);
			getCategoriesPreSignedUrl(categoryIds);
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.selectedRestaurant]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setRestaurant = (value: Nullable<TChangeCategoryPositionSelectedRestaurant>) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_RESTAURANT,
			payload: {
				selectedRestaurant: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ChangeCategoryPositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: ICategoryPositionItemV2[]) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangeCategoryPositionModalWindow) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisibleAdditionalRestaurantsModal: value,
				isApplyToOne: value ? false : state.isApplyToOne,
				selectedAdditionalRestaurants: value ? [] : state.selectedAdditionalRestaurants,
				selectSearchString: value ? '' : state.selectSearchString,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: TChangeCategoryPositionSelectedRestaurant[]) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				selectedAdditionalRestaurants: value,
			},
		});
	};

	const changeSelectSearchString = (value: string) => {
		dispatch({
			type: ChangeCategoryPositionActionTypes.CHANGE_SELECT_SEARCH_STRING,
			payload: {
				selectSearchString: value,
			},
		});
	};

	return (
		<ChangeCategoryPositionContext.Provider
			value={{
				setIDCustomer,
				setRestaurant,
				resetSelectedControls,
				setPositionsData,
				changeTypeOfModalWindow,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				changeSelectSearchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				selectedRestaurant: state.selectedRestaurant,
				positionsData: state.positionsData,
				typeOfModalWindow: state.typeOfModalWindow,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				selectSearchString: state.selectSearchString,
			}}>
			{children}
		</ChangeCategoryPositionContext.Provider>
	);
};
